export type Voice = 'piano' | 'nylon' | 'electric' | 'bowed' | 'voice' | 'handpan' | 'drums';

export interface InstrumentSound {
  setEnabled(on: boolean): void;
  play(voice: Voice, value: number, velocity: number): void;
  dispose(): void;
}

export function createInstrumentSound(): InstrumentSound {
  const ctx = new AudioContext();

  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(ctx.createDynamicsCompressor()).connect(ctx.destination);

  const reverb = ctx.createConvolver();
  reverb.buffer = impulse(ctx, 2.4);
  const wet = ctx.createGain();
  wet.gain.value = 0.3;
  reverb.connect(wet).connect(master);

  const strings = new Map<string, AudioBuffer>();
  const noise = noiseBuffer(ctx);
  const drive = driveCurve(3.5);
  let suspendTimer = 0;

  const out = (node: AudioNode, send = 1): void => {
    node.connect(master);
    if (send > 0) {
      const amount = ctx.createGain();
      amount.gain.value = send;
      node.connect(amount).connect(reverb);
    }
  };

  const envelope = (peak: number, attack: number, decay: number): GainNode => {
    const at = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(peak, at + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + attack + decay);
    return gain;
  };

  const tone = (
    type: OscillatorType,
    hz: number,
    target: AudioNode,
    length: number,
  ): OscillatorNode => {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = hz;
    osc.connect(target);
    osc.start();
    osc.stop(ctx.currentTime + length);
    return osc;
  };

  const plucked = (hz: number, velocity: number, electric: boolean): void => {
    const key = `${electric ? 'e' : 'n'}${hz}`;
    let buffer = strings.get(key);
    if (!buffer) {
      buffer = karplus(
        ctx,
        hz,
        electric ? 3 : 2.4,
        electric ? 0.998 : 0.995,
        electric ? 0.9 : 0.45,
      );
      strings.set(key, buffer);
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const level = ctx.createGain();
    level.gain.value = (electric ? 0.35 : 0.6) * velocity;
    const filter = ctx.createBiquadFilter();
    filter.frequency.value = electric ? 3800 : 3000;
    if (electric) {
      const shaper = ctx.createWaveShaper();
      shaper.curve = drive;
      source.connect(shaper).connect(filter);
    } else {
      source.connect(filter);
    }
    filter.connect(level);
    out(level, 0.6);
    source.start();
  };

  const piano = (hz: number, velocity: number): void => {
    const env = envelope(0.22 * velocity, 0.004, 2.6);
    const filter = ctx.createBiquadFilter();
    filter.frequency.setValueAtTime(5200, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 1.4);
    env.connect(filter);
    out(filter, 0.8);
    for (const [ratio, level] of [
      [1, 1],
      [2, 0.42],
      [3, 0.2],
      [4, 0.1],
      [5, 0.05],
    ] as const) {
      const partial = ctx.createGain();
      partial.gain.value = level;
      partial.connect(env);
      tone('sine', hz * ratio * (1 + 0.0005 * ratio * ratio), partial, 2.8);
    }
  };

  const handpan = (hz: number, velocity: number): void => {
    const filter = ctx.createBiquadFilter();
    filter.frequency.value = 4200;
    out(filter, 1.1);
    for (const [ratio, level, decay] of [
      [1, 0.26, 3.6],
      [1.003, 0.1, 3.2],
      [2, 0.12, 2.2],
      [3, 0.05, 1.2],
    ] as const) {
      const env = envelope(level * velocity, 0.008, decay);
      env.connect(filter);
      tone('sine', hz * ratio, env, decay + 0.1);
    }
  };

  const sustained = (hz: number, velocity: number, kind: 'bowed' | 'voice'): void => {
    const now = ctx.currentTime;
    const body = ctx.createGain();
    body.gain.setValueAtTime(0, now);
    body.gain.linearRampToValueAtTime(0.14 * velocity, now + (kind === 'voice' ? 0.09 : 0.14));
    body.gain.setValueAtTime(0.14 * velocity, now + 0.75);
    body.gain.exponentialRampToValueAtTime(0.0001, now + 1.3);

    const vibrato = ctx.createOscillator();
    vibrato.frequency.value = kind === 'voice' ? 5.2 : 5.8;
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(0, now);
    depth.gain.linearRampToValueAtTime(hz * 0.006, now + 0.35);
    vibrato.connect(depth);
    vibrato.start();
    vibrato.stop(now + 1.35);

    const shaped = ctx.createGain();
    if (kind === 'voice') {
      shaped.gain.value = 5;
      for (const [freq, q, level] of [
        [730, 9, 1],
        [1090, 12, 0.5],
        [2440, 20, 0.3],
      ] as const) {
        const formant = ctx.createBiquadFilter();
        formant.type = 'bandpass';
        formant.frequency.value = freq;
        formant.Q.value = q;
        const amount = ctx.createGain();
        amount.gain.value = level;
        body.connect(formant).connect(amount).connect(shaped);
      }
    } else {
      const filter = ctx.createBiquadFilter();
      filter.frequency.value = 2600;
      const resonance = ctx.createBiquadFilter();
      resonance.type = 'peaking';
      resonance.frequency.value = 280;
      resonance.gain.value = 5;
      body.connect(filter).connect(resonance).connect(shaped);
    }
    out(shaped, 0.9);

    for (const detune of kind === 'voice' ? [0] : [-4, 4]) {
      const osc = tone('sawtooth', hz, body, 1.35);
      osc.detune.value = detune;
      depth.connect(osc.frequency);
    }
  };

  const drum = (piece: number, velocity: number): void => {
    const now = ctx.currentTime;
    const burst = (type: BiquadFilterType, freq: number, peak: number, decay: number): void => {
      const source = ctx.createBufferSource();
      source.buffer = noise;
      const filter = ctx.createBiquadFilter();
      filter.type = type;
      filter.frequency.value = freq;
      const env = envelope(peak * velocity, 0.002, decay);
      source.connect(filter).connect(env);
      out(env, 0.3);
      source.start(now);
      source.stop(now + decay + 0.05);
    };
    const thump = (from: number, to: number, peak: number, decay: number): void => {
      const env = envelope(peak * velocity, 0.003, decay);
      out(env, 0.25);
      const osc = tone('sine', from, env, decay + 0.05);
      osc.frequency.exponentialRampToValueAtTime(to, now + decay * 0.5);
    };

    if (piece === 0) thump(140, 44, 0.9, 0.45);
    if (piece === 1) {
      burst('bandpass', 1900, 0.5, 0.18);
      thump(200, 160, 0.25, 0.1);
    }
    if (piece === 2) burst('highpass', 7500, 0.25, 0.07);
    if (piece === 3) thump(230, 150, 0.5, 0.3);
    if (piece === 4) thump(130, 85, 0.6, 0.5);
    if (piece === 5) burst('highpass', 5200, 0.12, 1.4);
  };

  return {
    setEnabled(on) {
      window.clearTimeout(suspendTimer);
      if (on) {
        void ctx.resume();
        master.gain.setTargetAtTime(1, ctx.currentTime, 0.05);
      } else {
        master.gain.setTargetAtTime(0, ctx.currentTime, 0.15);
        suspendTimer = window.setTimeout(() => void ctx.suspend(), 800);
      }
    },

    play(voice, value, velocity) {
      if (ctx.state !== 'running') return;
      if (voice === 'piano') piano(value, velocity);
      if (voice === 'nylon') plucked(value, velocity, false);
      if (voice === 'electric') plucked(value, velocity, true);
      if (voice === 'handpan') handpan(value, velocity);
      if (voice === 'bowed' || voice === 'voice') sustained(value, velocity, voice);
      if (voice === 'drums') drum(value, velocity);
    },

    dispose() {
      window.clearTimeout(suspendTimer);
      void ctx.close();
    },
  };
}

function karplus(
  ctx: AudioContext,
  hz: number,
  seconds: number,
  damping: number,
  brightness: number,
): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * seconds);
  const period = Math.max(2, Math.round(ctx.sampleRate / hz));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  const ring = new Float32Array(period);
  let previous = 0;
  for (let i = 0; i < period; i += 1) {
    previous = previous * (1 - brightness) + (Math.random() * 2 - 1) * brightness;
    ring[i] = previous;
  }
  let index = 0;
  for (let i = 0; i < length; i += 1) {
    const next = (index + 1) % period;
    const current = ring[index] ?? 0;
    data[i] = current;
    ring[index] = damping * 0.5 * (current + (ring[next] ?? 0));
    index = next;
  }
  return buffer;
}

function noiseBuffer(ctx: AudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  return buffer;
}

function driveCurve(amount: number): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(1024);
  for (let i = 0; i < curve.length; i += 1) {
    curve[i] = Math.tanh(amount * ((i / (curve.length - 1)) * 2 - 1));
  }
  return curve;
}

function impulse(ctx: AudioContext, seconds: number): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3;
    }
  }
  return buffer;
}
